<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import { useMessages } from '@composables/use-messages'

const props = defineProps({
    mix: {
        type: Object,
        default: () => {},
    },
    artist: {
        type: Object,
        default: () => {},
    },
    show: {
        type: Object,
        default: () => {},
    },
    channel: {
        type: Object,
        default: () => {},
    },
    size: {
        type: String,
        default: 'small', // none, small, iconOnly, large
    },
    buttonType: {
        type: String,
        default: 'tag', // tag, transparent, primary
    },
    buttonTypeActive: {
        type: [String, Boolean],
        default: false,
    },
    displayCounter: {
        type: Boolean,
        default: true,
    },
    iconMain: {
        type: String,
        default: 'heart-outlined',
    },
    iconAlt: {
        type: String,
        default: 'heart-outlined',
    },
})

const store = useStore()
const router = useRouter()
const getMessageText = useMessages()

const countCache = ref(0)
const isReady = ref(true)

const favCount = computed(() => props.mix && props.mix.favorite_count)

const totalCount = computed(() => props.mix && favCount.value + countCache.value)

const isLoggedIn = computed(() => store.getters['auth/loggedIn'])

const isActive = computed(() => {
    if (props.mix) {
        return !!store.state.favorites.mixesList.find(el => el.id === props.mix.id)
    } else if (props.artist) {
        return !!store.state.favorites.artistsList.find(el => el.id === props.artist.id)
    } else if (props.show) {
        return !!store.state.favorites.showsList.find(el => el.id === props.show.id)
    } else if (props.channel) {
        return !!store.state.favorites.channelIds.find(el => el === props.channel.id)
    } else {
        return false
    }
})

const buttonTypeActiveEvaluated = computed(() => props.buttonTypeActive || props.buttonType)

function notify(messageKey, type) {
    store.dispatch('appearance/handleNotification', { message: getMessageText(messageKey), type })
}

function addMix() {
    store
        .dispatch('favorites/add', { type: 'mixes', id: props.mix.id })
        .then(_res => {
            notify('FAV_MIX_ADDED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeMix() {
    store
        .dispatch('favorites/remove', { type: 'mixes', id: props.mix.id })
        .then(_res => {
            notify('FAV_MIX_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addArtist() {
    store
        .dispatch('favorites/add', { type: 'artists', id: props.artist.id })
        .then(_res => {
            notify('FAV_ARTIST_ADDED', 'success')
        })
        .catch(_err => {
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeArtist() {
    store
        .dispatch('favorites/remove', { type: 'artists', id: props.artist.id })
        .then(_res => {
            notify('FAV_ARTIST_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addShow() {
    store
        .dispatch('favorites/add', { type: 'shows', id: props.show.id })
        .then(_res => {
            notify('FAV_SHOW_ADDED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeShow() {
    store
        .dispatch('favorites/remove', { type: 'shows', id: props.show.id })
        .then(_res => {
            notify('FAV_SHOW_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addChannel() {
    store.dispatch('favorites/toggleChannel', props.channel.id)
}

function removeChannel() {
    store.dispatch('favorites/toggleChannel', props.channel.id)
}

const add = computed(() => (props.mix ? addMix : props.artist ? addArtist : props.show ? addShow : addChannel))

const remove = computed(() =>
    props.mix ? removeMix : props.artist ? removeArtist : props.show ? removeShow : removeChannel
)

watch(isActive, () => {
    isActive.value ? countCache.value++ : countCache.value--
    isReady.value = true
})

function addRemoveFav() {
    if (isLoggedIn.value) {
        if (isReady.value) {
            isReady.value = false
            isActive.value ? remove.value() : add.value()
        }
    } else {
        router.push({ name: 'login' })
    }
}
</script>

<template>
    <BaseButton
        :active="isLoggedIn && isActive"
        :button-type="isActive ? buttonType : buttonTypeActiveEvaluated"
        :size="size"
        :class="[
            'base-favourites',
            buttonType === 'transparent' ? 'base-favourites-transparent' : '',
            isActive ? 'base-favourites-active' : '',
            (mix && mix.id) || (artist && artist.id) || (show && show.id) || (channel && channel.id)
                ? ''
                : 'base-favourites-inactive',
        ]"
        :icon="size === 'large' ? false : isActive ? iconMain : iconAlt"
        @click="addRemoveFav"
    >
        <slot v-if="!$slots['main'] && displayCounter && size !== 'none'">{{
            size !== 'icon-only' ? Number(totalCount > 0 ? totalCount : isActive ? 1 : 0).toLocaleString() : ''
        }}</slot>
        &nbsp;
        <slot
            v-if="!isActive"
            name="main"
        />
        <slot
            v-else
            name="alt"
        />
    </BaseButton>
</template>

<style lang="scss" scoped>
@import '@design';

.base-favourites {
    &-transparent {
        &:hover ::v-deep svg,
        &.base-favourites-active ::v-deep svg {
            fill: $c-cerise-red;
        }
    }
}

.base-favourites-inactive {
    pointer-events: none;
    opacity: 0.3;
    filter: grayscale(1);
}
</style>
